/**
 * **A gunner on something that is not an aeroplane** — the machine that
 * `airship-pass.ts` worked out for a Zeppelin's four stations, with the
 * Zeppelin's own numbers lifted out of it so a second tenant can use it.
 *
 * The second tenant is the escort sloop's pair of Maxims (`ship-pass.ts`), and
 * a Maxim in a splinter tub is the same problem a Maxim on a gasbag's crown is:
 * a mount with an arc, a belt that runs down, a hand that makes the same
 * mistake for a burst at a time, and a per-round scatter on top of it. What is
 * *not* shared is every number — range, effectiveness, belt depth, how well the
 * man shoots — and those are all in `DefensiveGunSpec`, so a reader can see at a
 * glance which parts of a defensive battery this port has recovered and which
 * parts each tenant states for itself.
 *
 * The whole argument for each mechanism is `airship-pass.ts`'s module note,
 * which is still where it belongs: it is written against `MG.DAT` and
 * `shooting.c`, and this file is only the shape of it. Four things that note
 * establishes and this file preserves exactly, because the draw order is a
 * behaviour and not a detail:
 *
 * 1. **Cadence off the tick number**, never an accumulator — `roundsAtTick`.
 * 2. **One aiming mistake per station, held for an epoch**, drawn magnitude
 *    then bearing, two draws per station, and only when the epoch turns over.
 * 3. **Two draws per round**, azimuth scatter then elevation scatter, always in
 *    that order and never on a tick that fires nothing.
 * 4. **Each station picks its own nearest target inside its own arc**, so a
 *    station with an empty arc spends nothing and the blind spot is a hole
 *    rather than a rule.
 */

import type { GunSpec } from "../flight/guns.ts";
import { addVec3, normalizeVec3, rotateVec3, inverseRotateVec3, type Vec3 } from "../flight/math3d.ts";
import type { RngBank, RngStreamName } from "../flight/rng.ts";
import type { Tunables } from "../flight/tunables.ts";
import { bamToRadians } from "../flight/units.ts";
import type { EncounterSide } from "./encounter-sides.ts";
import { aimPoint, subtractVec3, type CombatBody } from "./geometry.ts";
import type { RoundRecord } from "./rounds.ts";
import { aimMount, traverseDirection, traverseOf, type Traverse } from "./traverse.ts";

/** Everything about a defensive battery that is the *tenant's* rather than this machine's. */
export interface DefensiveGunSpec {
  readonly gun: GunSpec;
  /** Past this, the battery does not engage at all. */
  readonly engagementRangeM: number;
  /** Half-width of the per-round graze scatter, in BAM16 counts on each aim axis. */
  readonly grazeScatterBam: number;
  /** The standing aiming mistake, as a multiple of `tunables.aiAimErrorRad`. */
  readonly aimErrorScale: number;
  /** Rounds the battery fires between redraws of that mistake. */
  readonly aimEpochRounds: number;
  /** Ticks of phase between one station's cadence and the next, so they do not all fire together. */
  readonly stationPhaseTicks: number;
  readonly stream: RngStreamName;
}

/** The belts and hands of one thing that shoots. Mirrors the fields `EncounterTarget` already carries. */
export interface DefensiveGunner {
  readonly id: string;
  readonly side: EncounterSide;
  readonly body: CombatBody;
  readonly gunRoundsRemaining: readonly number[];
  readonly gunRoundsFired: number;
  readonly gunAimBias: readonly number[];
  readonly gunAimEpoch: number;
}

/** One aeroplane a defensive battery might shoot at. */
export interface DefensiveQuarry {
  readonly id: string;
  readonly side: EncounterSide;
  readonly body: CombatBody;
  readonly destroyed: boolean;
}

/** What one battery's belts and hands look like after the tick. */
export interface DefensiveBelt {
  readonly gunRoundsRemaining: readonly number[];
  readonly gunRoundsFired: number;
  readonly gunAimBias: readonly number[];
  readonly gunAimEpoch: number;
}

export interface DefensiveFireInput {
  readonly gunners: readonly DefensiveGunner[];
  readonly spec: DefensiveGunSpec;
  readonly quarry: readonly DefensiveQuarry[];
  readonly rng: RngBank;
  readonly tunables: Tunables;
  readonly tick: number;
  readonly dt: number;
}

export interface DefensiveFire {
  readonly rounds: readonly RoundRecord[];
  /** Keyed by gunner id. Absent for one that did not fire — nothing to write back. */
  readonly belts: ReadonlyMap<string, DefensiveBelt>;
}

const NO_FIRE: DefensiveFire = { rounds: [], belts: new Map() };

/**
 * Rounds one station puts up on this tick. Pure in the tick number, so a pass
 * replayed from a snapshot fires the same rounds on the same ticks without an
 * accumulator ever having been serialized.
 */
export function roundsAtTick(tick: number, dt: number, roundsPerSecond: number, phaseTicks: number): number {
  if (roundsPerSecond <= 0 || dt <= 0) return 0;
  const at = (t: number): number => Math.floor((t + phaseTicks) * dt * roundsPerSecond);
  return Math.max(0, at(tick + 1) - at(tick));
}

/**
 * One axis of graze scatter, radians. One draw gives both magnitude and sign —
 * the low bits are the mask the original applies, the bit above them is the
 * side it falls.
 */
function grazeScatterRad(rng: RngBank, spec: DefensiveGunSpec): number {
  const draw = rng.nextUint32(spec.stream);
  const magnitude = draw & spec.grazeScatterBam;
  const signBit = 32 - Math.clz32(spec.grazeScatterBam);
  return bamToRadians(magnitude) * ((draw >>> signBit) & 1 ? -1 : 1);
}

/** A fresh standing aiming mistake per station: two draws each, magnitude then bearing, uniform in the disc. */
function drawAimBias(rng: RngBank, stations: number, spec: DefensiveGunSpec, tunables: Tunables): number[] {
  const span = Math.max(0, tunables.aiAimErrorRad) * spec.aimErrorScale;
  const bias: number[] = [];
  for (let station = 0; station < stations; station += 1) {
    const magnitude = span * rng.nextFloat(spec.stream);
    const bearing = 2 * Math.PI * rng.nextFloat(spec.stream);
    bias.push(magnitude * Math.cos(bearing), magnitude * Math.sin(bearing));
  }
  return bias;
}

export interface StationTarget {
  readonly quarry: DefensiveQuarry;
  /** The lead solution, as a unit direction in the gunner's own body frame. */
  readonly aim: Vec3;
  readonly rangeM: number;
}

/**
 * The nearest live enemy inside the range gate that **this station can be
 * brought onto**, or `null`.
 *
 * The aim is the lead solution `aimPoint` gives every other shooter in the
 * port, and the arc test is applied to the *led* direction rather than to the
 * aeroplane: a gunner traverses onto where his target is going, and if that
 * point is outside his mount he cannot take the shot even though the aeroplane
 * itself is inside it.
 *
 * Pure — no draws — which is what lets `ship-pass.ts` call it a second time to
 * find where a mount is *pointing* on a tick it does not fire.
 */
export function stationTarget(
  body: CombatBody,
  mountIndex: number,
  spec: DefensiveGunSpec,
  quarry: readonly DefensiveQuarry[],
  side: EncounterSide,
  tunables: Tunables,
): StationTarget | null {
  const mount = spec.gun.mounts[mountIndex];
  if (!mount) return null;
  let best: StationTarget | null = null;
  for (const entry of quarry) {
    if (entry.side === side || entry.destroyed) continue;
    const offset = subtractVec3(entry.body.position, body.position);
    const rangeM = Math.hypot(offset.x, offset.y, offset.z);
    if (rangeM <= 0 || rangeM >= spec.engagementRangeM) continue;
    if (best !== null && rangeM >= best.rangeM) continue;
    const lead = aimPoint(body, entry.body, tunables.gunMuzzleSpeedMps);
    const aim = normalizeVec3(
      inverseRotateVec3(body.orientation, subtractVec3(lead.point, body.position)),
      { x: 0, y: 0, z: -1 },
    );
    if (!aimMount(mount, traverseOf(aim)).withinArc) continue;
    best = { quarry: entry, aim, rangeM };
  }
  return best;
}

/** Where one station is pointing this tick, or `null` when nothing is in its arc. */
export function stationAim(
  body: CombatBody,
  mountIndex: number,
  spec: DefensiveGunSpec,
  quarry: readonly DefensiveQuarry[],
  side: EncounterSide,
  tunables: Tunables,
): Traverse | null {
  const engaged = stationTarget(body, mountIndex, spec, quarry, side, tunables);
  if (!engaged) return null;
  const mount = spec.gun.mounts[mountIndex];
  return mount ? aimMount(mount, traverseOf(engaged.aim)).traverse : null;
}

/** Everything one gunner mutates while its stations take their turn. */
interface GunnerPass {
  readonly gunner: DefensiveGunner;
  readonly remaining: number[];
  readonly bias: readonly number[];
  readonly rounds: RoundRecord[];
  roundsFired: number;
  spent: boolean;
}

function fireStation(pass: GunnerPass, station: number, input: DefensiveFireInput, tracerInterval: number): void {
  const { spec } = input;
  const mount = spec.gun.mounts[station];
  if (!mount) return;
  const belt = pass.remaining[station] ?? 0;
  if (belt <= 0) return;
  const wanted = roundsAtTick(input.tick, input.dt, spec.gun.roundsPerSecond, station * spec.stationPhaseTicks);
  if (wanted <= 0) return;
  // A station that cannot be brought onto anything spends nothing. This is the
  // whole of the blind spot: no target inside the arc, no rounds.
  const engaged = stationTarget(pass.gunner.body, station, spec, input.quarry, pass.gunner.side, input.tunables);
  if (!engaged) return;

  const count = Math.min(wanted, belt);
  const requested = traverseOf(engaged.aim);
  const biasAz = pass.bias[station * 2] ?? 0;
  const biasEl = pass.bias[station * 2 + 1] ?? 0;
  for (let index = 0; index < count; index += 1) {
    // Two draws per round, azimuth then elevation, always in that order.
    const azimuthRad = requested.azimuthRad + biasAz + grazeScatterRad(input.rng, spec);
    const elevationRad = requested.elevationRad + biasEl + grazeScatterRad(input.rng, spec);
    const aimed = aimMount(mount, { azimuthRad, elevationRad });
    const roundIndex = pass.roundsFired + index;
    pass.rounds.push({
      shooterId: pass.gunner.id,
      roundIndex,
      from: addVec3(pass.gunner.body.position, rotateVec3(pass.gunner.body.orientation, mount.bodyOffsetM)),
      direction: rotateVec3(pass.gunner.body.orientation, traverseDirection(aimed.traverse)),
      spawnTick: input.tick,
      damageRaw: spec.gun.damageRaw,
      tracer: roundIndex % tracerInterval === 0,
    });
  }
  pass.remaining[station] = belt - count;
  pass.roundsFired += count;
  pass.spent = true;
}

/**
 * One tick of defensive fire for every gunner handed in.
 *
 * Draws from the tenant's stream **exactly twice per round fired**, plus two
 * per station on the ticks an epoch turns over, and not at all otherwise — the
 * same contract `resolveGunnery` keeps for `gunnery`, and for the same reason:
 * the stream must advance as a function of rounds fired, never of whether the
 * geometry happened to be interesting.
 */
export function defensiveFire(input: DefensiveFireInput): DefensiveFire {
  if (input.gunners.length === 0 || input.quarry.length === 0) return NO_FIRE;
  const { spec } = input;
  const stations = spec.gun.mounts.length;
  const tracerInterval = Math.max(1, Math.round(input.tunables.gunTracerInterval));
  const rounds: RoundRecord[] = [];
  const belts = new Map<string, DefensiveBelt>();

  for (const gunner of input.gunners) {
    // The gunners' hands, settled once for the tick. The epoch is a pure
    // function of `gunRoundsFired`, so how many draws this takes depends on the
    // state and never on the geometry — the stream's contract.
    const epoch = Math.floor(gunner.gunRoundsFired / spec.aimEpochRounds);
    const stale = epoch !== gunner.gunAimEpoch || gunner.gunAimBias.length !== stations * 2;
    const bias = stale ? drawAimBias(input.rng, stations, spec, input.tunables) : [...gunner.gunAimBias];
    const pass: GunnerPass = {
      gunner,
      remaining: [...gunner.gunRoundsRemaining],
      bias,
      rounds,
      roundsFired: gunner.gunRoundsFired,
      spent: stale,
    };
    for (let station = 0; station < stations; station += 1) fireStation(pass, station, input, tracerInterval);
    if (pass.spent) {
      belts.set(gunner.id, {
        gunRoundsRemaining: pass.remaining,
        gunRoundsFired: pass.roundsFired,
        gunAimBias: bias,
        gunAimEpoch: epoch,
      });
    }
  }

  return rounds.length === 0 && belts.size === 0 ? NO_FIRE : { rounds, belts };
}
